<script lang="ts">
  import { authState } from '../../lib/auth.svelte';
  import { queueWelcomeEmail, type WelcomeEmailData } from '../../lib/api';
  import { onMount } from 'svelte';

  // Welcome email resend state
  let resendingEmail = $state(false);
  let resendSuccess = $state(false);
  let resendError = $state('');
  let lastResendTime = $state<number | null>(null);
  const RESEND_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes

  // Derived state for cooldown
  let cooldownRemaining = $state(0);
  let cooldownInterval: ReturnType<typeof setInterval> | null = null;

  function startCooldownTimer() {
    if (cooldownInterval) clearInterval(cooldownInterval);
    cooldownInterval = setInterval(() => {
      if (lastResendTime) {
        const elapsed = Date.now() - lastResendTime;
        const remaining = Math.max(0, RESEND_COOLDOWN_MS - elapsed);
        cooldownRemaining = remaining;
        if (remaining === 0 && cooldownInterval) {
          clearInterval(cooldownInterval);
          cooldownInterval = null;
        }
      }
    }, 1000);
  }

  async function resendWelcomeEmail() {
    if (!authState.user?.email) {
      resendError = 'No email address found';
      return;
    }

    // Check cooldown
    if (lastResendTime && Date.now() - lastResendTime < RESEND_COOLDOWN_MS) {
      return;
    }

    resendingEmail = true;
    resendSuccess = false;
    resendError = '';

    try {
      const welcomeData: WelcomeEmailData = {
        email: authState.user.email,
        firstName: authState.user.displayName?.split(' ')[0] || '',
        source: 'admin_approval',
      };

      const result = await queueWelcomeEmail(welcomeData);

      if (result.success) {
        resendSuccess = true;
        lastResendTime = Date.now();
        cooldownRemaining = RESEND_COOLDOWN_MS;
        startCooldownTimer();
      } else {
        resendError = result.error || 'Failed to send email';
      }
    } catch (err: any) {
      resendError = err.message || 'Failed to send email';
    } finally {
      resendingEmail = false;
    }
  }

  function formatCooldown(ms: number): string {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  onMount(() => {
    return () => {
      if (cooldownInterval) clearInterval(cooldownInterval);
    };
  });
</script>

<div class="help-link-section">
  {#if resendSuccess}
    <div class="resend-success">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
      </svg>
      Welcome email sent! Check your inbox.
      {#if cooldownRemaining > 0}
        <span class="cooldown-text">Resend available in {formatCooldown(cooldownRemaining)}</span>
      {/if}
    </div>
  {:else if resendError}
    <div class="resend-error">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      {resendError}
      <button class="retry-link" onclick={resendWelcomeEmail}>Try again</button>
    </div>
  {:else}
    <button
      class="help-link"
      onclick={resendWelcomeEmail}
      disabled={resendingEmail || cooldownRemaining > 0}
    >
      {#if resendingEmail}
        <span class="spinner-small"></span>
        Sending...
      {:else if cooldownRemaining > 0}
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
        </svg>
        Resend available in {formatCooldown(cooldownRemaining)}
      {:else}
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
        </svg>
        Didn't get your welcome email? Click here to resend!
      {/if}
    </button>
  {/if}
</div>

<style>
  .help-link-section {
    margin-top: 2rem;
    text-align: center;
  }

  .help-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: transparent;
    border: none;
    color: #6b7280;
    font-size: 0.9rem;
    cursor: pointer;
    transition: color 0.2s;
  }

  .help-link:hover:not(:disabled) {
    color: #4A7DFF;
  }

  .help-link:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .help-link svg {
    width: 18px;
    height: 18px;
  }

  .resend-success {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    color: #059669;
    font-size: 0.9rem;
  }

  .resend-success svg {
    width: 24px;
    height: 24px;
  }

  .cooldown-text {
    color: #6b7280;
    font-size: 0.8rem;
  }

  .resend-error {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    color: #dc2626;
    font-size: 0.9rem;
  }

  .resend-error svg {
    width: 24px;
    height: 24px;
  }

  .retry-link {
    background: none;
    border: none;
    color: #4A7DFF;
    font-size: 0.85rem;
    cursor: pointer;
    text-decoration: underline;
  }

  .retry-link:hover {
    color: #6B5CE7;
  }

  .spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top-color: #4A7DFF;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
